import * as React from "react";

import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: design-system-v2; AvatarFallback now supports two opt-in modes:
//   1. `name` prop — auto-derive initials and hash-tint bg/text from a 4-pair palette
//   2. `unknown` prop — dashed cream/forest-300 circle with "?" for missing identities
// Existing call sites that pass `children` continue to render with the legacy
// forest-100 / forest-700 default. The new props are purely additive.

const Avatar = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
    <AvatarPrimitive.Root
        ref={ref}
        className={cn(
            "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
            className,
        )}
        {...props}
    />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Image>,
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
    <AvatarPrimitive.Image
        ref={ref}
        className={cn("aspect-square h-full w-full", className)}
        {...props}
    />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// AIDEV-NOTE: 4-pair palette mirrors DSAvatar in design-system-v2.jsx.
// Indices: forest, terra, earth-sage, earth-rust. Hashing matches the spec.
const HASH_PALETTE: ReadonlyArray<readonly [string, string]> = [
    ["bg-brand-forest-100", "text-brand-forest-700"],
    ["bg-brand-terra-100", "text-brand-terra-700"],
    ["bg-[#e8e5d4]", "text-[#70765a]"], // earth-sage
    ["bg-brand-cream-300", "text-[#973D2A]"], // earth-rust
] as const;

function deriveInitials(input: string): string {
    if (input.includes("@")) return input[0]?.toUpperCase() ?? "?";
    const parts = input.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return parts
        .slice(0, 2)
        .map((s) => s[0]!)
        .join("")
        .toUpperCase();
}

function hashPaletteFor(name: string): readonly [string, string] {
    const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
    return HASH_PALETTE[hash % HASH_PALETTE.length]!;
}

type AvatarFallbackBaseProps = React.ComponentPropsWithoutRef<
    typeof AvatarPrimitive.Fallback
>;

interface AvatarFallbackExtraProps {
    /**
     * When provided AND `children` is omitted, derive initials from the name
     * and apply a hash-tinted bg/text from the 4-pair palette. If `children`
     * is also provided, `children` wins (legacy behavior).
     */
    name?: string;
    /**
     * When true, render the dashed cream/forest-300 circle with "?" — the
     * "unknown identity" variant. Overrides `name` and `children`.
     */
    unknown?: boolean;
}

export type AvatarFallbackProps = AvatarFallbackBaseProps &
    AvatarFallbackExtraProps;

const AvatarFallback = React.forwardRef<
    React.ElementRef<typeof AvatarPrimitive.Fallback>,
    AvatarFallbackProps
>(({ className, children, name, unknown, ...props }, ref) => {
    // AIDEV-NOTE: priority — unknown > children > name. Preserves legacy callers
    // (which only pass children) and lets explicit className still win via cn().

    if (unknown) {
        return (
            <AvatarPrimitive.Fallback
                ref={ref}
                className={cn(
                    "flex h-full w-full items-center justify-center rounded-full font-bold text-xs bg-brand-cream-300 border border-dashed border-brand-forest-300 text-brand-forest-600",
                    className,
                )}
                {...props}
            >
                ?
            </AvatarPrimitive.Fallback>
        );
    }

    if (children === undefined && name) {
        const [bgClass, fgClass] = hashPaletteFor(name);
        return (
            <AvatarPrimitive.Fallback
                ref={ref}
                className={cn(
                    "flex h-full w-full items-center justify-center rounded-full font-bold text-xs",
                    bgClass,
                    fgClass,
                    className,
                )}
                {...props}
            >
                {deriveInitials(name)}
            </AvatarPrimitive.Fallback>
        );
    }

    return (
        <AvatarPrimitive.Fallback
            ref={ref}
            className={cn(
                "flex h-full w-full items-center justify-center rounded-full bg-white border-2 border-brand-forest-100 font-bold text-brand-forest-700 text-xs",
                className,
            )}
            {...props}
        >
            {children}
        </AvatarPrimitive.Fallback>
    );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
